/**
 * entitlements.ts
 * Maps a subscription tier to what the teacher can do in GuroHub.
 * Single source of truth for feature gating — read via useEntitlements().
 *
 * Gating table (confirm/adjust with the product owner):
 *   Feature              | free | premium | premium+
 *   ---------------------|------|---------|---------
 *   Classes              |  1   |   5     | ∞
 *   Students per class   |  30  |  200    | ∞
 *   Quiz tools           | MC   |  all    | all
 *   AI Quiz Builder      |  ✗   |   ✓     | ✓
 *   Slide mirroring      |  ✗   |   ✗     | ✓
 *   Live leaderboard     |  ✓   |   ✓     | ✓
 */

import type { Tier } from '@db/firebase';
import type { ActivityType } from '@types-local/quiz.types';
import { useAppStore } from '@store/appStore';

const INF = Number.POSITIVE_INFINITY;

export interface Entitlements {
  maxClasses: number;
  maxStudentsPerClass: number;
  tools: ActivityType[];
  ai: boolean;
  slideMirror: boolean;
}

const ALL_TOOLS: ActivityType[] = [
  'multiple_choice', 'word_cloud', 'short_answer', 'slide_drawing', 'media_upload', 'video_quiz',
];

export const TIER_LIMITS: Record<Tier, Entitlements> = {
  free: {
    maxClasses: 1,
    maxStudentsPerClass: 30,
    tools: ['multiple_choice'],
    ai: false,
    slideMirror: false,
  },
  premium: {
    maxClasses: 5,
    maxStudentsPerClass: 200,
    tools: ALL_TOOLS,
    ai: true,
    slideMirror: false,
  },
  premium_plus: {
    maxClasses: INF,
    maxStudentsPerClass: INF,
    tools: ALL_TOOLS,
    ai: true,
    slideMirror: true,
  },
};

export const TIER_LABELS: Record<Tier, string> = {
  free: 'Free',
  premium: 'Premium',
  premium_plus: 'Premium+',
};

/** Returns the current teacher's entitlements based on their tier. */
export function useEntitlements(): Entitlements & { tier: Tier } {
  const tier = useAppStore((s) => s.tier);
  return { ...TIER_LIMITS[tier], tier };
}

/** True if the given activity tool is allowed on the current tier. */
export function toolAllowed(tier: Tier, tool: ActivityType): boolean {
  return TIER_LIMITS[tier].tools.includes(tool);
}
